import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { requestAgent, uploadImages } from '../api';
import AddressAutocomplete from './AddressAutocomplete';

// 03_requirements_missing.md story 3 - a rental owner asks the website owner to onboard a
// site-visit agent for their area. The owner can't create staff accounts directly, so they submit
// everything an agent account needs - including MANDATORY Aadhaar KYC (number + card scan) and the
// person's own address - and the website owner approves it. Adapted from jewelry's
// RequestDeliveryPersonModal.
const empty = {
  name: '', email: '', phone: '', serviceArea: '',
  aadhaarNumber: '', aadhaarCardUrl: '',
  // staff_selfie_kyc.md - a camera selfie of the person is mandatory too.
  selfieUrl: '',
  // v5_fix_issues #1 - a driving-licence photo is mandatory for an agent (delivery-role) too.
  drivingLicenceNumber: '', drivingLicenceUrl: '',
  line1: '', line2: '', area: '', city: '', state: '', pincode: '',
  lat: null, lng: null, placeId: '', formatted: '',
  note: ''
};

const ADDRESS_KEYS = ['line1', 'line2', 'area', 'city', 'state', 'pincode', 'lat', 'lng', 'placeId', 'formatted'];

export default function RequestAgentModal({ show, onHide, onSubmitted }) {
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => { if (show) { setF(empty); setError(''); } }, [show]);

  const handleFileTo = (key, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const [url] = await uploadImages([file]);
      setF((p) => ({ ...p, [key]: url }));
    } catch {
      setError(`Could not upload the ${label} image (JPEG/PNG).`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!f.aadhaarCardUrl) { setError('Upload a scan/photo of the Aadhaar card.'); return; }
    if (!f.selfieUrl) { setError('Take a selfie photo with the camera.'); return; }
    if (!f.drivingLicenceUrl) { setError('Upload a photo of the driving licence.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await requestAgent({
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), serviceArea: f.serviceArea.trim(),
        kyc: {
          aadhaarNumber: f.aadhaarNumber.trim(), aadhaarCardUrl: f.aadhaarCardUrl,
          selfieUrl: f.selfieUrl,
          drivingLicenceNumber: f.drivingLicenceNumber.trim(), drivingLicenceUrl: f.drivingLicenceUrl
        },
        address: {
          line1: f.line1.trim(), line2: f.line2.trim(), area: (f.area || '').trim(),
          city: f.city.trim(), state: f.state.trim(), pincode: f.pincode.trim(),
          lat: f.lat ?? null, lng: f.lng ?? null, placeId: f.placeId || '', formatted: f.formatted || ''
        },
        note: f.note.trim()
      });
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not submit the request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Request a site-visit agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            The website owner reviews this and, on approval, creates the account. Aadhaar KYC, a
            camera selfie, a driving-licence photo and an address are mandatory for an agent.
          </p>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          <div className="row g-2">
            <div className="col-md-6"><Form.Control size="sm" placeholder="Full name" required value={f.name} onChange={set('name')} /></div>
            <div className="col-md-6"><Form.Control size="sm" type="email" placeholder="Email" required value={f.email} onChange={set('email')} /></div>
            <div className="col-md-6"><Form.Control size="sm" placeholder="Phone" value={f.phone} onChange={set('phone')} /></div>
            <div className="col-md-6"><Form.Control size="sm" placeholder="Service area (e.g. Bandra, Mumbai)" value={f.serviceArea} onChange={set('serviceArea')} /></div>
          </div>

          <h6 className="text-uppercase text-muted small mt-3">Aadhaar KYC</h6>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Control size="sm" placeholder="Aadhaar number (12 digits)" required inputMode="numeric" maxLength={12} value={f.aadhaarNumber} onChange={set('aadhaarNumber')} />
            </div>
            <div className="col-md-6 d-flex align-items-center gap-2">
              <Form.Control size="sm" type="file" accept="image/*" onChange={handleFileTo('aadhaarCardUrl', 'Aadhaar')} />
              {f.aadhaarCardUrl && !uploading && <span className="text-success small">Uploaded ✓</span>}
            </div>
          </div>

          {/* staff_selfie_kyc.md - mandatory camera selfie. `capture="user"` opens the front
              camera on mobile; on desktop it's a normal file picker. */}
          <h6 className="text-uppercase text-muted small mt-3">Selfie</h6>
          <div className="row g-2">
            <div className="col-md-6 d-flex align-items-center gap-2">
              <Form.Control size="sm" type="file" accept="image/*" capture="user" onChange={handleFileTo('selfieUrl', 'selfie')} />
              {uploading && <Spinner animation="border" size="sm" />}
              {f.selfieUrl && !uploading && <span className="text-success small">Uploaded ✓</span>}
            </div>
            <div className="col-md-6 small text-muted align-self-center">A clear, face-forward photo taken with the camera.</div>
          </div>

          {/* v5_fix_issues #1 - mandatory driving-licence photo. */}
          <h6 className="text-uppercase text-muted small mt-3">Driving licence</h6>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Control size="sm" placeholder="Licence number (optional)" value={f.drivingLicenceNumber} onChange={set('drivingLicenceNumber')} />
            </div>
            <div className="col-md-6 d-flex align-items-center gap-2">
              <Form.Control size="sm" type="file" accept="image/*" onChange={handleFileTo('drivingLicenceUrl', 'driving-licence')} />
              {uploading && <Spinner animation="border" size="sm" />}
              {f.drivingLicenceUrl && !uploading && <span className="text-success small">Uploaded ✓</span>}
            </div>
          </div>

          <h6 className="text-uppercase text-muted small mt-3">Address</h6>
          <AddressAutocomplete
            size="sm"
            showState
            required
            idPrefix="req-agent-addr"
            value={ADDRESS_KEYS.reduce((o, k) => ({ ...o, [k]: f[k] }), {})}
            onChange={(a) => setF((p) => ({ ...p, ...a }))}
          />

          <Form.Control as="textarea" rows={2} className="mt-2" size="sm" placeholder="Note for the website owner (optional)" value={f.note} onChange={set('note')} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting || uploading}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
